import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";

import { useMutatePostResolvePrismUser } from "../../data/services/salesAdminMappingService";

/**
 * "Add by user id" for the Sales Admin Mapping pickers.
 *
 * The pickers are fed from PrismUserDetail, which the background sync only ever
 * populates from ClientAssignment's SalesPerson and BenefitRep columns. A newly
 * created sales-support user holds no client assignments, so the sync has no id
 * to enumerate them by and they never appear in the list - which is exactly the
 * population this feature exists to map.
 *
 * Verify-then-add rather than plain freeform: a mistyped id would otherwise
 * create a mapping that silently matches nobody, and the Sales Admin would log
 * in to zero clients with nothing on screen explaining why. Showing the
 * resolved name and email before committing makes the typo visible.
 *
 * A successful resolve also caches the user server-side, so they appear in the
 * grid and in every future dropdown - the list self-heals as admins use it.
 */
const PrismUserLookup = ({ onResolved, label = "Add by user id" }) => {
  const [open, setOpen] = React.useState(false);
  const [userId, setUserId] = React.useState("");
  const [resolved, setResolved] = React.useState(null);
  const [error, setError] = React.useState(null);

  const resolveHandler = useMutatePostResolvePrismUser();

  const reset = () => {
    setUserId("");
    setResolved(null);
    setError(null);
  };

  const handleVerify = async () => {
    setResolved(null);
    setError(null);

    try {
      const response = await resolveHandler.mutateAsync({ userId: userId.trim() });

      if (response?.data?.found && response.data.user) {
        setResolved(response.data.user);
      } else {
        setError(`No PrismHR user with id "${userId.trim()}".`);
      }
    } catch {
      // The server already reports an unknown id as found: false, so reaching
      // here means the request itself failed. Say so rather than implying the
      // id was wrong.
      setError("Could not reach PrismHR. Try again in a moment.");
    }
  };

  const handleAdd = () => {
    onResolved(resolved);
    reset();
    setOpen(false);
  };

  if (!open) {
    return (
      <Button fillMode={"flat"} onClick={() => setOpen(true)}>
        {label}
      </Button>
    );
  }

  return (
    <div
      style={{
        border: "1px solid #ddd",
        borderRadius: 4,
        padding: 12,
        marginTop: 8,
      }}
    >
      <label style={{ display: "block", fontWeight: 500, marginBottom: 4 }}>
        PrismHR user id
      </label>
      <Input
        placeholder={"PrismHR user id"}
        value={userId}
        onChange={(e) => {
          setUserId(e.value ?? e.target?.value ?? "");
          // Editing the id invalidates the previous verification. Without
          // this, verifying one id and then typing a different one left the
          // old "Add" button live, adding the user who was never re-verified.
          setResolved(null);
          setError(null);
        }}
      />

      {resolved && (
        <p style={{ marginTop: 8 }}>
          {`Verified: ${resolved.userName}`}
          {resolved.userEmail ? ` (${resolved.userEmail})` : ""}
        </p>
      )}

      {error && (
        <p style={{ marginTop: 8, color: "#c00" }}>{error}</p>
      )}

      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <Button
          onClick={handleVerify}
          disabled={userId.trim().length === 0 || resolveHandler.isLoading}
        >
          {"Verify"}
        </Button>

        {resolved && (
          <Button themeColor={"primary"} onClick={handleAdd}>
            {"Add"}
          </Button>
        )}

        <Button
          fillMode={"flat"}
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          {"Cancel"}
        </Button>
      </div>
    </div>
  );
};

export default PrismUserLookup;
